/**
 * App-profile ownership tracking
 *
 * Tracks which profiles have "claimed" each app (i.e. have data in it).
 * Used to auto-switch profiles when navigating to a tool whose data lives
 * under a different profile, and to drive the per-profile visible app list.
 *
 * All data is persisted in localStorage — no server sync needed since this
 * is device-local UX state that resets cleanly on a fresh login anyway.
 */

// ── Ownership map ─────────────────────────────────────────────────────────────

const OWNERS_KEY = 'yada:app-owners';

function readOwners(): Record<string, string[]> {
  try {
    return JSON.parse(localStorage.getItem(OWNERS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeOwners(data: Record<string, string[]>): void {
  localStorage.setItem(OWNERS_KEY, JSON.stringify(data));
}

/** Return the list of profile IDs that have used a given app. */
export function getAppOwners(appId: string): string[] {
  return readOwners()[appId] ?? [];
}

/**
 * Record that a profile has used an app.
 * Idempotent — safe to call on every app activation.
 */
export function addAppOwner(appId: string, profileId: string): void {
  const data = readOwners();
  const owners = data[appId] ?? [];
  if (!owners.includes(profileId)) {
    data[appId] = [...owners, profileId];
    writeOwners(data);
  }
}

/** Return all app IDs that have been used by a given profile. */
export function getProfileApps(profileId: string): string[] {
  const data = readOwners();
  return Object.entries(data)
    .filter(([, owners]) => owners.includes(profileId))
    .map(([appId]) => appId);
}

// ── Per-profile workspace overrides ──────────────────────────────────────────
//
// When the user picks "share data" during a profile switch, we store a pointer
// to the original profile's workspace so the new profile reads the same CRDT
// documents. The storageProvider reads this before falling back to the current
// workspace context.

const WS_OVERRIDE_PREFIX = 'yada:app-workspace-override:';

function wsOverrideKey(profileId: string, appId: string): string {
  return `${WS_OVERRIDE_PREFIX}${profileId}:${appId}`;
}

/** Return the workspace ID override for a profile-app pair, or null. */
export function getAppWorkspaceOverride(profileId: string, appId: string): string | null {
  return localStorage.getItem(wsOverrideKey(profileId, appId));
}

/** Store a workspace ID override for a profile-app pair. */
export function setAppWorkspaceOverride(
  profileId: string,
  appId: string,
  workspaceId: string
): void {
  localStorage.setItem(wsOverrideKey(profileId, appId), workspaceId);
}

/**
 * Remap all app ownerships and workspace overrides from old profile IDs to new profile IDs.
 */
export function remapProfileAppOwnership(idMapping: Record<string, string>): void {
  const owners = readOwners();
  let modified = false;

  for (const [appId, profileIds] of Object.entries(owners)) {
    const remapped = profileIds.map(pid => idMapping[pid] || pid);
    const unique = Array.from(new Set(remapped));
    if (JSON.stringify(unique) !== JSON.stringify(profileIds)) {
      owners[appId] = unique;
      modified = true;
    }
  }

  if (modified) {
    writeOwners(owners);
  }

  // Remap workspace overrides
  if (typeof localStorage !== 'undefined') {
    const keysToRemap: Array<{ key: string; fromPid: string; appId: string }> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(WS_OVERRIDE_PREFIX)) {
        const rest = k.slice(WS_OVERRIDE_PREFIX.length);
        const [fromPid, appId] = rest.split(':');
        if (fromPid && idMapping[fromPid]) {
          keysToRemap.push({ key: k, fromPid, appId });
        }
      }
    }

    for (const { key, fromPid, appId } of keysToRemap) {
      const val = localStorage.getItem(key);
      localStorage.removeItem(key);
      if (val && appId) {
        setAppWorkspaceOverride(idMapping[fromPid], appId, val);
      }
    }
  }
}

/**
 * Clean up app owners that no longer exist in validProfiles.
 * If fallbackProfileId is provided, orphan ownerships are assigned to fallbackProfileId.
 */
export function cleanupOrphanedAppOwners(validProfileIds: string[], fallbackProfileId?: string): void {
  const owners = readOwners();
  const validSet = new Set(validProfileIds);
  let modified = false;

  for (const [appId, profileIds] of Object.entries(owners)) {
    const filtered = profileIds.filter(pid => validSet.has(pid));
    if (filtered.length === 0 && fallbackProfileId && validSet.has(fallbackProfileId)) {
      filtered.push(fallbackProfileId);
    }
    if (JSON.stringify(filtered) !== JSON.stringify(profileIds)) {
      owners[appId] = filtered;
      modified = true;
    }
  }

  if (modified) {
    writeOwners(owners);
  }
}
